import { createContext, useContext, useMemo } from "react";

export interface FeaturesContextValue<T> {
    features: T;
}

const FeaturesContext = createContext<FeaturesContextValue<unknown> | undefined>(undefined);

export function FeaturesProvider<T>({ children, features }: React.PropsWithChildren<FeaturesContextValue<T>>) {
    // Memoize on the `features` reference so the context value keeps a stable identity across
    // parent re-renders. Consumers only re-render when `features` actually changes (e.g. when
    // flags resolve in useFlags), never on unrelated parent churn.
    const value = useMemo<FeaturesContextValue<T>>(() => ({ features }), [features]);
    return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}

export function useFeatures<T>(): FeaturesContextValue<T> {
    const result = useContext(FeaturesContext) as FeaturesContextValue<T> | undefined;
    if (result === undefined) throw new Error("Hook used outside of the FeaturesProvider");
    return result;
}
